/* eslint-disable @next/next/no-img-element */
import { Button } from 'antd';
import React from 'react';
import classNames from 'classnames';
import CommonJs from '@/utils/commonJs';
import { appendStaticFileAuthParams } from '@/components/common/FileAndLinkUpload';
import { CommentOutlined } from '@ant-design/icons';
import '../../index.css';

export interface DiscussItem {
  id: string | number;
  /** Id người tạo (nguoiTao) — dùng map với danh sách mention */
  authorId?: string | number;
  author: string;
  time: string;
  content: string;
  parentId?: string;
  depth?: number;
  avatarUrl?: string;
  isReply?: boolean;
  attachments?: {
    url: string;
    name: string;
  }[];
}

export type ThaoLuanMentionRowForDiscuss = {
  id: string;
  label: string;
  payloadToken?: string;
};

/** Chuỗi hiển thị trong chip mention (luôn có một @ đầu, dùng label đã format) */
export function resolveMentionTokenForDisplay(
  mentionWithAt: string,
  rows?: ThaoLuanMentionRowForDiscuss[] | null
): string {
  if (!rows?.length) return mentionWithAt;
  const trimmed = String(mentionWithAt || '').trim();
  if (!trimmed.startsWith('@')) return mentionWithAt;

  const withPayload = [...rows]
    .filter((r) => r.payloadToken)
    .sort((a, b) => (b.payloadToken!.length || 0) - (a.payloadToken!.length || 0));
  for (const r of withPayload) {
    if (trimmed === r.payloadToken) {
      return `@${r.label}`;
    }
  }

  const inner = trimmed.slice(1);
  const direct = rows.find((r) => String(r.id) === inner);
  if (direct) return `@${direct.label}`;

  const segments = inner.split('_').filter(Boolean);
  const bySegLen = [...segments].sort((a, b) => b.length - a.length);
  for (const seg of bySegLen) {
    const row = rows.find((r) => String(r.id) === seg);
    if (row) return `@${row.label}`;
  }

  return mentionWithAt;
}

export function resolveDiscussAuthorLabel(
  item: DiscussItem,
  mentionRows?: ThaoLuanMentionRowForDiscuss[] | null
): string {
  if (!mentionRows?.length) return item.author;
  const aid =
    item.authorId != null && String(item.authorId) !== ''
      ? String(item.authorId)
      : '';
  if (!aid) return item.author;
  const row = mentionRows.find((r) => String(r.id) === aid);
  return row?.label ?? item.author;
}

interface DiscussComponentProps {
  data: DiscussItem[];
  /** Danh sách option mention (id → label), ví dụ replyMentionOptionRows */
  thaoLuanMentionFiltered?: ThaoLuanMentionRowForDiscuss[];
  /** Message vừa gửi — highlight tạm thời */
  highlightDiscussId?: string | null;
  onReplyClick?: (item: DiscussItem) => void;
  activeReplyId?: string | null;
  renderReplyComposer?: (item: DiscussItem) => React.ReactNode;
}

const getInitials = (name: string) => {
  const text = String(name || '').trim();
  if (!text) return '';
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return text.substring(0, 2).toUpperCase();
};

const renderEmployeeAvatar = (
  item: DiscussItem,
  size: number,
  displayName: string
) => {
  const initials = getInitials(displayName);
  const backgroundColor = CommonJs.genColorByName(displayName || String(item.id));

  return (
    <div
      className="rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 overflow-hidden"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: item.avatarUrl ? '#ffffff' : backgroundColor,
        fontSize: size <= 24 ? 10 : 12,
      }}
    >
      {item.avatarUrl ? (
        <img
          src={item.avatarUrl}
          alt={displayName}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const parent = target.parentElement;
            if (parent) {
              parent.innerHTML = initials;
              parent.style.backgroundColor = backgroundColor;
            }
          }}
        />
      ) : (
        initials
      )}
    </div>
  );
};

const renderMentionHighlightedContent = (
  content: string,
  mentionRows?: ThaoLuanMentionRowForDiscuss[] | null
) => {
  const raw = String(content || '');
  if (!raw) return '';

  const parts = raw.split(/(\s+)/);
  return parts.map((part, index) => {
    if (!part) return null;
    if (part.startsWith('@') && !/\s/.test(part)) {
      const display = resolveMentionTokenForDisplay(part, mentionRows);
      return (
        <span
          key={`mention-${index}`}
          className="inline-block px-1 rounded bg-blue-50 text-blue-600 font-medium"
        >
          {display}
        </span>
      );
    }
    return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
  });
};

export const DiscussComponent: React.FC<DiscussComponentProps> = ({
  data,
  thaoLuanMentionFiltered,
  highlightDiscussId,
  onReplyClick,
  activeReplyId,
  renderReplyComposer,
}) => {
  return (
    <div className="cvda-discuss-list  border border-[#e5e7eb] rounded-2xl p-1">
      {data.map((item) => {
        const authorLabel = resolveDiscussAuthorLabel(item, thaoLuanMentionFiltered);
        const depth = item.depth || 0;
        const isReply = depth > 0 || item.isReply;
        const attachments = item.attachments || [];
        const isNew = highlightDiscussId != null && String(highlightDiscussId) === String(item.id);

        return (
          <div
            key={item.id}
            className="cvda-discuss-comment-row "
            style={{ marginLeft: depth > 0 ? depth * 26 : 0 }}
          >
            {renderEmployeeAvatar(item, isReply ? 20 : 28, authorLabel)}
            <div className="cvda-discuss-comment-body">
              <div className={classNames('cvda-discuss-comment-bubble', isNew && 'is-new')}>
                <div className="flex flex-wrap items-center gap-1">
                  <span className="cvda-discuss-comment-author">{authorLabel}</span>
                  {isNew ? <span className="cvda-discuss-comment-badge-new">Mới</span> : null}
                </div>
                <p className="cvda-discuss-comment-text">
                  {renderMentionHighlightedContent(item.content, thaoLuanMentionFiltered)}
                </p>
                {attachments.length > 0 && (
                  <div className="mt-2 flex flex-col gap-1">
                    {attachments.map((file) => {
                      const url = file.url;
                      const authUrl = appendStaticFileAuthParams(url);
                      const name = file.name;
                      const lower = url.toLowerCase();
                      const extMatch = lower.split('.').pop() || '';
                      const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extMatch);

                      if (isImage) {
                        return (
                          <div key={url} className="mt-1">
                            <a
                              href={authUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-block overflow-hidden rounded-md border border-gray-200"
                            >
                              <img
                                src={authUrl}
                                alt={name}
                                style={{ maxWidth: 160, maxHeight: 120, display: 'block' }}
                              />
                            </a>
                            <div className="mt-0.5 max-w-[160px] truncate text-[11px] text-gray-500">
                              {name}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={url}
                          className="inline-flex max-w-[220px] items-center rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-700"
                        >
                          <span className="flex-1 truncate">{name}</span>
                          <a
                            href={authUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="ml-2 text-blue-500"
                          >
                            Xem
                          </a>
                          <a href={authUrl} download className="ml-2 text-gray-500">
                            Tải
                          </a>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="cvda-discuss-comment-actions">
                {item.time ? <span className="text-[11px] text-gray-400">{item.time}</span> : null}
                <Button
                  type="link"
                  size="small"
                  className="h-auto p-0 text-[11px] leading-none"
                  style={{ fontSize: 11, lineHeight: '12px', height: 16 }}
                  onClick={() => onReplyClick?.(item)}
                  icon={<CommentOutlined />}
                >
                  Phản hồi
                </Button>
              </div>
              {activeReplyId && String(activeReplyId) === String(item.id) && renderReplyComposer
                ? <div className="mt-2">{renderReplyComposer(item)}</div>
                : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
